import {
  getUserInfo,
  setLocalEmployeeId,
  setLocalUserInfo,
} from "@/lib/storeManager";
import { get } from "@/services/axiosUitils";
import type { QueryClient } from "@tanstack/react-query";

const basePath = "/pts_app/current-user-details";

export const CURRENT_USER_DETAILS_NAVBAR_KEY = [
  "current-user-details",
  "navbar",
] as const;

export const CURRENT_USER_DETAILS_PROFILE_KEY = [
  "current-user-details",
  "profile",
] as const;

export type CurrentUserPersonalDetails = {
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
};

export type CurrentUserEmploymentDetails = {
  designation?: string | null;
  department?: string | null;
  biometric_id?: string | null;
};

export type CurrentUserAddressContact = {
  email_address?: string | null;
  phone_number?: string | null;
  emergency_contact?: string | null;
  current_address?: string | null;
  permanent_address?: string | null;
};

export type CurrentUserDetailsResponse = {
  success?: { code?: string; message?: string };
  employee_id?: string;
  full_name?: string;
  initials?: string;
  personal_details?: CurrentUserPersonalDetails;
  employment_details?: CurrentUserEmploymentDetails;
  address_contact?: CurrentUserAddressContact;
  img?: string;
  image?: string;
};

export type CurrentUserEmployee = {
  id: string;
  img?: string;
  full_name?: string;
  initials?: string;
  personal_details?: CurrentUserPersonalDetails;
  employment_details?: CurrentUserEmploymentDetails;
  address_contact?: CurrentUserAddressContact;
  [key: string]: unknown;
};

export type ProfileEmployeeRecord = Record<string, unknown> & {
  id?: string;
  employee_id?: string;
  full_name?: string;
  initials?: string;
};

export type ProfileViewField = {
  key: string;
  label: string;
  type?: "date" | "text";
};

export type ProfileViewSection = {
  label: string;
  dataKey: "personal_details" | "employment_details" | "address_contact";
  fields: ProfileViewField[];
};

/** Section-wise field definitions aligned with current-user-details API response. */
export const PROFILE_VIEW_SECTIONS: ProfileViewSection[] = [
  {
    label: "Personal Details",
    dataKey: "personal_details",
    fields: [
      { key: "first_name", label: "First Name" },
      { key: "middle_name", label: "Middle Name" },
      { key: "last_name", label: "Last Name" },
      { key: "date_of_birth", label: "Date of Birth", type: "date" },
      { key: "gender", label: "Gender" },
    ],
  },
  {
    label: "Employment Details",
    dataKey: "employment_details",
    fields: [
      { key: "designation", label: "Designation" },
      { key: "department", label: "Department" },
      { key: "biometric_id", label: "Biometric ID" },
    ],
  },
  {
    label: "Address & Contact",
    dataKey: "address_contact",
    fields: [
      { key: "email_address", label: "Email Address" },
      { key: "phone_number", label: "Phone Number" },
      { key: "emergency_contact", label: "Emergency Contact" },
      { key: "current_address", label: "Current Address" },
      { key: "permanent_address", label: "Permanent Address" },
    ],
  },
];

export const formatProfileDisplayValue = (
  value: unknown,
  type?: ProfileViewField["type"]
): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (type === "date") {
    try {
      const date = new Date(String(value));
      if (isNaN(date.getTime())) {
        return "-";
      }
      const d = String(date.getDate()).padStart(2, "0");
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const y = date.getFullYear();
      return `${d}-${m}-${y}`;
    } catch {
      return "-";
    }
  }

  const text = String(value);
  if (text.toLowerCase() === "male" || text.toLowerCase() === "female") {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  return text;
};

/**
 * Maps /pts_app/current-user-details/ response to flat profile fields
 * used by EmployeeProfilePage (schema field_name lookups).
 */
export const mapCurrentUserDetailsToProfile = (
  response: CurrentUserDetailsResponse | null | undefined
): ProfileEmployeeRecord | null => {
  if (!response?.employee_id) {
    return null;
  }

  const personal = response.personal_details ?? {};
  const employment = response.employment_details ?? {};
  const contact = response.address_contact ?? {};

  return {
    id: response.employee_id,
    employee_id: response.employee_id,
    full_name: response.full_name,
    initials: response.initials,
    img: response.img ?? response.image,
    image: response.img ?? response.image,
    personal_details: personal,
    employment_details: employment,
    address_contact: contact,
    // Personal Details
    first_name: personal.first_name,
    firstname: personal.first_name,
    middle_name: personal.middle_name,
    last_name: personal.last_name,
    lastname: personal.last_name,
    date_of_birth: personal.date_of_birth,
    dob: personal.date_of_birth,
    gender: personal.gender,
    // Employment Details
    designation: employment.designation,
    department: employment.department,
    biometric_id: employment.biometric_id,
    biometricid: employment.biometric_id,
    biometric: employment.biometric_id,
    // Address & Contact
    email: contact.email_address,
    email_address: contact.email_address,
    emailaddress: contact.email_address,
    phone_number: contact.phone_number,
    phone: contact.phone_number,
    mobile: contact.phone_number,
    contact_number: contact.phone_number,
    emergency_contact: contact.emergency_contact,
    current_address: contact.current_address,
    permanent_address: contact.permanent_address,
    address: contact.current_address,
  };
};

/** Resolve a schema field value from the mapped profile record. */
export const getProfileFieldValue = (
  profile: ProfileEmployeeRecord | null | undefined,
  fieldName: string
): unknown => {
  if (!profile || !fieldName) {
    return undefined;
  }

  const direct =
    profile[fieldName] ??
    profile[fieldName.toLowerCase()] ??
    profile[fieldName.replace(/_/g, "")];

  if (direct !== null && direct !== undefined && direct !== "") {
    return direct;
  }

  const lower = fieldName.toLowerCase();
  const personal = profile.personal_details as
    | CurrentUserPersonalDetails
    | undefined;
  const employment = profile.employment_details as
    | CurrentUserEmploymentDetails
    | undefined;
  const contact = profile.address_contact as
    | CurrentUserAddressContact
    | undefined;

  if (
    lower.includes("first") &&
    (lower.includes("name") || lower === "fname" || lower === "firstname")
  ) {
    return personal?.first_name;
  }
  if (lower.includes("middle") && lower.includes("name")) {
    return personal?.middle_name;
  }
  if (
    lower.includes("last") &&
    (lower.includes("name") || lower === "lname" || lower === "lastname")
  ) {
    return personal?.last_name;
  }
  if (lower.includes("dob") || lower.includes("birth")) {
    return personal?.date_of_birth;
  }
  if (lower.includes("gender")) {
    return personal?.gender;
  }
  if (lower.includes("designation")) {
    return employment?.designation;
  }
  if (lower.includes("department")) {
    return employment?.department;
  }
  if (lower.includes("biometric")) {
    return employment?.biometric_id;
  }
  if (lower.includes("email")) {
    return contact?.email_address;
  }
  if (
    lower.includes("phone") ||
    lower.includes("mobile") ||
    (lower.includes("contact") && !lower.includes("emergency"))
  ) {
    return contact?.phone_number;
  }
  if (lower.includes("emergency")) {
    return contact?.emergency_contact;
  }
  if (lower.includes("current") && lower.includes("address")) {
    return contact?.current_address;
  }
  if (lower.includes("permanent") && lower.includes("address")) {
    return contact?.permanent_address;
  }
  if (lower.includes("address") && !lower.includes("email")) {
    return contact?.current_address ?? contact?.permanent_address;
  }

  return undefined;
};

/** Save full_name / initials / employee_id to localStorage after login. */
export const persistCurrentUserDetailsSession = (
  details: CurrentUserDetailsResponse | null | undefined
): CurrentUserDetailsResponse | null => {
  if (!details?.employee_id) {
    return null;
  }

  const displayName = getCurrentUserDisplayName(details);
  const stored = getUserInfo() ?? {};

  setLocalUserInfo({
    ...stored,
    full_name: displayName || stored.full_name,
    ...(details.initials ? { initials: details.initials } : {}),
  });
  setLocalEmployeeId(details.employee_id);

  return details;
};

/** Prime react-query cache so navbar/profile do not wait for a refetch. */
export const cacheCurrentUserDetailsQueries = (
  queryClient: QueryClient,
  details: CurrentUserDetailsResponse
) => {
  queryClient.setQueryData([...CURRENT_USER_DETAILS_NAVBAR_KEY], details);
  queryClient.setQueryData([...CURRENT_USER_DETAILS_PROFILE_KEY], details);
  queryClient.setQueryData(["current-user-details"], details);
};

/** Fetch current-user-details, persist session, and cache for immediate UI after login. */
export const fetchPersistAndCacheCurrentUserDetails = async (
  queryClient?: QueryClient
): Promise<CurrentUserDetailsResponse | null> => {
  const details = (await get(`${basePath}/`, {
    notifyMessage: false,
  })) as CurrentUserDetailsResponse;

  if (!details?.employee_id) {
    return null;
  }

  persistCurrentUserDetailsSession(details);

  if (queryClient) {
    cacheCurrentUserDetailsQueries(queryClient, details);
  }

  return details;
};

export type EmployeeSelectOption = { value: string; label: string };

/** Single employee dropdown option from current-user-details (Sprint Task Allocation, etc.). */
export const employeeOptionFromCurrentUserDetails = (
  details: CurrentUserDetailsResponse | null | undefined
): EmployeeSelectOption | null => {
  if (!details?.employee_id) {
    return null;
  }
  const label = getCurrentUserDisplayName(details) || details.employee_id;
  return { value: details.employee_id, label };
};

/** Display name for navbar / header from current-user-details response. */
export const getCurrentUserDisplayName = (
  response: CurrentUserDetailsResponse | null | undefined
): string => {
  if (response?.full_name?.trim()) {
    return response.full_name.trim();
  }
  const personal = response?.personal_details;
  if (personal?.first_name || personal?.last_name) {
    return [personal.first_name, personal.last_name].filter(Boolean).join(" ");
  }
  return "";
};

/**
 * Normalizes /pts_app/current-user-details/ response for login employee ID storage.
 */
export const normalizeCurrentUserEmployee = (
  response: unknown
): CurrentUserEmployee | null => {
  const profile = mapCurrentUserDetailsToProfile(
    response as CurrentUserDetailsResponse
  );
  if (!profile?.id) {
    return null;
  }

  return {
    ...profile,
    id: String(profile.id),
    img:
      (profile.img as string | undefined) ??
      (profile.image as string | undefined),
  };
};

class PtsCurrentUserDetailsService {
  /**
   * GET /pts_app/current-user-details/
   * Uses Bearer token, X-Tenant-Id, and X-User-Timezone from axios interceptors.
   */
  getCurrentUserDetails = async (): Promise<CurrentUserDetailsResponse> => {
    return (await get(`${basePath}/`, {
      notifyMessage: false,
    })) as CurrentUserDetailsResponse;
  };

  getCurrentUserEmployee = async (): Promise<CurrentUserEmployee | null> => {
    const response = await this.getCurrentUserDetails();
    return normalizeCurrentUserEmployee(response);
  };

  getProfileEmployee = async (): Promise<ProfileEmployeeRecord | null> => {
    const response = await this.getCurrentUserDetails();
    return mapCurrentUserDetailsToProfile(response);
  };
}

const instance = new PtsCurrentUserDetailsService();
export default instance;
